/**
 * THE canonical charset for a transaction-attribute key
 * (`bat.transaction_attributes.key`, and by FK `bat.transactions.attribute`).
 *
 * This module exists because the super-admin CREATOR and the customer
 * transaction VALIDATOR used to carry two different regexes: the admin accepted
 * digits (`/^[a-z][a-z0-9-]*$/`) while the customer forbade them
 * (`/^[a-z_-]*$/`). An admin could therefore create an attribute (e.g.
 * `qa631-digit2`) that the API served, the customer modal offered as a
 * selectable option, and every save rejected with 400 — with no remedy for the
 * user. Both sides now import from HERE. Never re-declare the pattern.
 *
 * Why THIS charset (kebab-case, letter-first, digits allowed, no underscores):
 *   - It accepts 100% of the keys that exist today — verified against prod
 *     (`bat`: 63 rows), dev (`bat_dev`: 61) and e2e (`bat_e2e`: 63): every key
 *     is kebab-case, ZERO contain an underscore, ZERO contain a digit. Same for
 *     the distinct `bat.transactions.attribute` values in use. Nothing that is
 *     already stored becomes unsavable.
 *   - Kebab-case is the project-wide convention for these keys: every seed
 *     (`seeds/transaction-attributes/*.seed.ts`) and every import alias
 *     (`IMPORT_ATTRIBUTE_ALIAS_TO_CANONICAL`) is kebab. Allowing underscores
 *     would let an admin mint two spellings of one concept
 *     (`staking-reward` / `staking_reward`) that the alias map already treats
 *     as the same thing.
 *   - Digits stay legal because the admin UI has always advertised them
 *     ("Lowercase letters, digits and dashes") and keys such as `web3-reward`
 *     are legitimate.
 *   - The key doubles as an i18next lookup segment (`attributes.<key>.name`,
 *     see CsvUploadForm) and travels in URL query strings (attribute filter).
 *     The charset therefore excludes `.` (i18next key separator), `:` (i18next
 *     namespace separator) and every character that would need
 *     percent-encoding.
 */

/**
 * Canonical key shape: starts with a lowercase letter, then lowercase letters,
 * digits or hyphens. The `+` encodes the 2-character minimum
 * (`TRANSACTION_ATTRIBUTE_KEY_MIN_LENGTH`).
 */
export const TRANSACTION_ATTRIBUTE_KEY_PATTERN = /^[a-z][a-z0-9-]+$/

/** Minimum key length — encoded by the `+` in the pattern above. */
export const TRANSACTION_ATTRIBUTE_KEY_MIN_LENGTH = 2

/**
 * Max key length. MUST stay in lockstep with the DB column
 * `varchar('key', { length: 50 })` (data-source.schema.ts): a longer key would
 * pass validation and crash the INSERT with a raw 500.
 */
export const TRANSACTION_ATTRIBUTE_KEY_MAX_LENGTH = 50

/**
 * The same rule, but the EMPTY string also passes.
 *
 * The customer transaction schemas need this: a submit with no type selected
 * must trip ONLY the "required" message. If the shape rule also fired, the UI
 * (which renders the last issue per field) would leak the cryptic charset rule
 * instead of "select a transaction type". Derived from
 * `TRANSACTION_ATTRIBUTE_KEY_PATTERN.source` so the two can never drift.
 */
export const TRANSACTION_ATTRIBUTE_KEY_OR_EMPTY_PATTERN = new RegExp(
  `^$|${TRANSACTION_ATTRIBUTE_KEY_PATTERN.source}`,
)

/** Full contract: canonical shape AND within the DB column bounds. */
export function isValidTransactionAttributeKey(candidate: string): boolean {
  return (
    candidate.length <= TRANSACTION_ATTRIBUTE_KEY_MAX_LENGTH &&
    TRANSACTION_ATTRIBUTE_KEY_PATTERN.test(candidate)
  )
}
